import React, { useState } from 'react';
import { products, Product } from 'data/shop';
import Image from 'next/image';
// import image1 from 'images/Ecoflow/Delta/1front.png';
// import image2 from 'images/Ecoflow/Delta/2front.png';
// import image3 from 'images/Ecoflow/Delta/3back.png';
// import image4 from 'images/Ecoflow/Delta/4back.png';
// import image5 from 'images/Ecoflow/Delta/5left.png';
// import image6 from 'images/Ecoflow/Delta/6right.png';
import {
  IoIosArrowDroprightCircle,
  IoIosArrowDropleftCircle,
} from 'react-icons/io';
import { GetStaticProps } from 'next';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import { ProductJsonLd } from 'next-seo';
import MetaTags from '@/components/Head/MetaTags';
import PaymentBadges from '@/components/Shop/PaymentBadges';

export const getStaticProps: GetStaticProps = async () => {
  // Find the product with the slug 'delta-pro-portabse-power-station'
  const selectedProduct = products.find(
    (product: Product) => product.slug === 'delta-max-2000-power-station'
  );

  if (!selectedProduct) {
    return {
      notFound: true, // Return a 404 page if the product is not found
    };
  }

  return {
    props: {
      product: selectedProduct,
    },
  };
};

type Props = {
  product: Product;
};

const ProductPage = ({ product }: Props) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = product.images;

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <>
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Shop',
            href: '/shop',
          },
          {
            name: product.title,
            href: '/shop/' + product.slug,
          },
        ]}
      />
      <MetaTags
        title={'EcoFlow ' + product.title}
        description={'Shop the EcoFlow ' + product.title + ' on Energypal'}
      />
      <ProductJsonLd
        productName={product.title}
        brand={'EcoFlow'}
        manufacturerName={'EcoFlow'}
        images={images}
        offers={[
          {
            price: parseInt(product.price, 10),
            highPrice: product.salePrice,
            url: `https://energypal.com/shop/${product.slug}`,
            priceCurrency: 'USD',
            priceValidUntil: '2025-01-01',
            itemCondition: 'https://schema.org/NewCondition',
            availability: 'https://schema.org/InStock',
            seller: {
              '@type': 'Organization',
              name: 'EnergyPal',
              url: 'https://energypal.com/shop',
            },
          },
        ]}
      />
      <div className="max-w-7xl mx-auto">
        <div className="h-auto flex flex-col md:flex-row md:w-full md:space-x-8">
          {/* Left half for main image and gallery */}
          <div className="flex-1 w-full">
            <div
              className="max-w-screen-xl mx-auto p-8 h-[450px] md:h-[600px] relative md:sticky md:top-12 mb-12"
              style={{}}
            >
              {/* Main image */}
              <Image
                src={images[currentImageIndex]}
                alt="Product"
                className="object-fit p-8"
                layout="fill"
                objectFit="cover"
              />
              {/* Left arrow */}
              <IoIosArrowDropleftCircle
                className="absolute top-1/2 left-0 transform -translate-y-1/2 w-10 h-10 hover:cursor-pointer opacity-70 text-slate-800"
                onClick={prevImage}
              />
              {/* Right arrow */}
              <IoIosArrowDroprightCircle
                onClick={nextImage}
                className="absolute top-1/2 right-0 transform -translate-y-1/2 w-10 h-12 hover:cursor-pointer opacity-70 text-slate-800"
              />
            </div>
            {/* Gallery */}
            <div className="flex justify-center relative p-4 md:sticky md:top-3/4">
              {images.map((image, index) => (
                <div
                  key={index}
                  className={` h-20 w-20 sticky top-1/4 ${
                    index === currentImageIndex ? 'border-indigo-500' : ''
                  }`}
                >
                  <Image
                    src={image}
                    alt={`Product Image ${index + 1}`}
                    className="object-cover border-2 border-transparent p-2 "
                    layout="fill"
                    objectFit="cover"
                    onClick={() => setCurrentImageIndex(index)}
                  />
                  {index === currentImageIndex && (
                    <div className="absolute inset-0 flex justify-center items-center border-[1px] border-black bg-opacity-50"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Right half for product details */}
          <div className="flex-1 p-8 flex flex-col pt-16 ">
            <div>
              {/* <div className="uppercase tracking-wide text-sm text-indigo-500 font-semibold">
              Product Category
            </div> */}
              <h2 className="text-gray-900 text-3xl font-semibold mb-2">
                {product.title}
              </h2>
              <span className="text-gray-900 font-semibold ml-2 text-sm p-1 rounded-md bg-green-200">
                Save ${product.savings}
              </span>
              <div className="flex items-center mb-0 pb-0">
                {/* <span className="text-gray-700">Price:</span> */}
                <span className="text-gray-900 font-semibold ml-2 text-lg">
                  {product.price}
                </span>
                <span className="text-gray-500 font-semibold ml-2 text-lg line-through p-1 rounded-md">
                  {product.salePrice}
                </span>
              </div>
              <span className="text-gray-500 font-bold text-sm mb-4 ml-2 pt-0">
                Tax Included + Free Shipping
              </span>
              {/* <h3 className="text-gray-700 text-lg font-medium ml-2 mb-2 mt-4">
                Quantity
              </h3>
              <QuantitySelector /> */}
              <a href={product.buyLink}>
                <button className="bg-primary text-white px-12 py-4 rounded-md hover:bg-indigo-600 mb-4 w-full mt-2 text-lg font-medium">
                  Buy Now
                </button>
              </a>
              <PaymentBadges />
              <hr />
              {/* Accordion for description, technical specs, and warranty info */}
              <p className="mt-4">{product.intro}</p>

              <ul className="list-disc ml-4 my-4">
                {product.points.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
              <Accordion
                warranty={product.warranty}
                specs={product.techStats}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductPage;

interface AccordionProps {
  warranty: string | undefined;
  specs: string[];
}

const Accordion: React.FC<AccordionProps> = ({ warranty, specs }) => {
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isTechSpecsOpen, setIsTechSpecsOpen] = useState(false);
  const [isWarrantyInfoOpen, setIsWarrantyInfoOpen] = useState(false);

  const renderIcon = (isOpen: boolean) => {
    return isOpen ? (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 inline-block ml-auto"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M3.707 5.293a1 1 0 0 1 1.414 0L10 10.586l4.793-4.793a1 1 0 0 1 1.414 1.414l-5.5 5.5a1 1 0 0 1-1.414 0l-5.5-5.5a1 1 0 0 1 0-1.414z"
          clipRule="evenodd"
        />
      </svg>
    ) : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 inline-block ml-auto"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M3 7a1 1 0 0 1 1-1h12a1 1 0 0 1 0 2H4a1 1 0 0 1-1-1zM3 11a1 1 0 0 1 1-1h8a1 1 0 0 1 0 2H4a1 1 0 0 1-1-1zM3 15a1 1 0 0 1 1-1h8a1 1 0 0 1 0 2H4a1 1 0 0 1-1-1z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  const description = (
    <div>
      <p>
        <strong>X-STREAM FAST CHARGE</strong>
        RAPID 0-80% IN 65 MINS
        <br />
        Fully charge via a wall outlet in a rapid 1.8hrs.
      </p>
      <br />

      <p>
        <strong>POWER PRETTY MUCH ANYTHING </strong>
        <br />
        2400W output is enough to power almost anything. Turn on X-Boost for
        3400W, enough for window AC, heaters or dryers.
      </p>
      <br />

      <p>
        <strong>FOUR EASY WAYS TO CHARGE</strong>
        <br />
        WALL, SOLAR, CAR OR SMART GEN <br />
        Choose between solar panels, wall outlets or the EcoFlow Smart Generator
        to charge up.
      </p>
      <br />

      <p>
        <strong>ECOFLOW SMART GENERATOR FOR EMERGENCY BACKUP</strong>
        <br />
        In an emergency, use the EcoFlow Smart Generator to charge up during a
        blackout. It’s 25% more efficient than traditional gas generators.
      </p>
    </div>
  );

  return (
    <div className="w-full mx-auto">
      <div className=" rounded-md overflow-hidden">
        {/* Description Accordion */}
        <div>
          <input
            type="checkbox"
            id="accordion1"
            className="hidden"
            checked={isDescriptionOpen}
            onChange={() => setIsDescriptionOpen(!isDescriptionOpen)}
          />
          <label
            htmlFor="accordion1"
            className="block cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-3 mb-1 rounded-t-md text-gray-800 font-semibold flex items-center"
          >
            Description {renderIcon(isDescriptionOpen)}
          </label>
          <div
            className={`overflow-hidden transition-all duration-500 ${
              isDescriptionOpen ? 'max-h-100' : 'max-h-0'
            }`}
          >
            <div className="px-4 py-2 text-gray-700">{description}</div>
          </div>
        </div>

        {/* Technical Specs Accordion */}
        <div>
          <input
            type="checkbox"
            id="accordion2"
            className="hidden"
            checked={isTechSpecsOpen}
            onChange={() => setIsTechSpecsOpen(!isTechSpecsOpen)}
          />
          <label
            htmlFor="accordion2"
            className="block cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-3 mb-1 text-gray-800 font-semibold flex items-center"
          >
            Technical Specs {renderIcon(isTechSpecsOpen)}
          </label>
          <div
            className={`overflow-hidden transition-all duration-500 ${
              isTechSpecsOpen ? 'max-h-100' : 'max-h-0'
            }`}
          >
            <div className="px-4 py-2 text-gray-700">
              <ul className="list-disc pl-2">
                {specs.map((spec, index) => (
                  <li key={index}>{spec}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Warranty Info Accordion */}
        <div>
          <input
            type="checkbox"
            id="accordion3"
            className="hidden"
            checked={isWarrantyInfoOpen}
            onChange={() => setIsWarrantyInfoOpen(!isWarrantyInfoOpen)}
          />
          <label
            htmlFor="accordion3"
            className="block cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-3 mb-2 rounded-b-md text-gray-800 font-semibold flex items-center"
          >
            Warranty {renderIcon(isWarrantyInfoOpen)}
          </label>
          <div
            className={`overflow-hidden transition-all duration-500 ${
              isWarrantyInfoOpen ? 'max-h-100' : 'max-h-0'
            }`}
          >
            <div className="px-4 py-2 text-gray-700">{warranty}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
